import * as React from "react";
import { cn } from "../../lib/utils";
import { Phone } from "lucide-react";

export interface EmbedHeaderProps {
  logoUrl: string;
  defaultLogoUrl?: string;
  logoAlt?: string;
  /** When set, the logo becomes a link to the fleet's public booking page. */
  logoHref?: string;
  /** Open the logo link in a new tab (mirrors the `open_in_new_tab` param). */
  openInNewTab?: boolean;
  phone?: string | null;
  countryCode?: string | null;
  children?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
}

const headerStyles = `
    .embed-header-mobile {
      display: flex !important;
    }
    .embed-header-desktop {
      display: none !important;
    }
    @media (min-width: 1024px) {
      .embed-header-mobile {
        display: none !important;
      }
      .embed-header-desktop {
        display: flex !important;
      }
    }
  `;

export function EmbedHeader({
  logoUrl,
  defaultLogoUrl,
  logoAlt = "Logo",
  logoHref,
  openInNewTab = false,
  phone,
  countryCode,
  children,
  className,
  isLoading = false,
}: EmbedHeaderProps) {
  const formattedPhone = phone ? `${countryCode || "1"} ${phone}` : null;
  const dialNumber = formattedPhone?.replace(/\s/g, "") ?? null;

  // When the fleet has a public booking page, the logo links to it. Default
  // target is `_top` so it escapes the embed iframe; `open_in_new_tab` opens it
  // in a new tab instead.
  const wrapLogo = (logo: React.ReactNode) =>
    logoHref ? (
      <a
        href={logoHref}
        target={openInNewTab ? "_blank" : "_top"}
        rel={openInNewTab ? "noopener noreferrer" : undefined}
        aria-label={logoAlt}
        className="inline-flex items-center"
      >
        {logo}
      </a>
    ) : (
      logo
    );

  return (
    <header
      className={cn(
        "w-full bg-white border-b border-gray-100 px-4 py-3 lg:px-6",
        className,
      )}
    >
      <style dangerouslySetInnerHTML={{ __html: headerStyles }} />
      <div className="embed-header-mobile flex-col gap-3">
        <div className="flex items-center justify-between">
          {isLoading ? (
            <div className="h-8 w-[120px] bg-gray-200 rounded animate-pulse" />
          ) : (
            wrapLogo(
              <img
                src={logoUrl}
                alt={logoAlt}
                className="h-8 w-auto max-w-[140px] object-contain"
                onError={(e) => {
                  // Attempt the default logo at most once, then hide the image.
                  // Comparing currentTarget.src (absolute) against a relative
                  // defaultLogoUrl never matches and would loop forever.
                  const img = e.currentTarget;
                  if (defaultLogoUrl && img.dataset.fallbackTried !== "1") {
                    img.dataset.fallbackTried = "1";
                    img.src = defaultLogoUrl;
                  } else {
                    img.style.display = "none";
                  }
                }}
              />,
            )
          )}
          {formattedPhone && (
            <a
              href={`tel:${dialNumber}`}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 whitespace-nowrap transition-colors"
            >
              <Phone className="h-4 w-4" />
            </a>
          )}
        </div>
        {children && <div className="w-full">{children}</div>}
      </div>

      <div className="embed-header-desktop items-center gap-0">
        <div className="flex-shrink-0 w-[180px]">
          {isLoading ? (
            <div className="h-10 w-[150px] bg-gray-200 rounded animate-pulse" />
          ) : (
            wrapLogo(
              <img
                src={logoUrl}
                alt={logoAlt}
                className="h-10 w-auto max-w-[180px] object-contain"
                onError={(e) => {
                  // Attempt the default logo at most once, then hide the image.
                  // Comparing currentTarget.src (absolute) against a relative
                  // defaultLogoUrl never matches and would loop forever.
                  const img = e.currentTarget;
                  if (defaultLogoUrl && img.dataset.fallbackTried !== "1") {
                    img.dataset.fallbackTried = "1";
                    img.src = defaultLogoUrl;
                  } else {
                    img.style.display = "none";
                  }
                }}
              />,
            )
          )}
        </div>

        <div className="flex-1 flex justify-center min-w-0">{children}</div>

        <div className="flex-shrink-0 w-[180px] flex items-center justify-end">
          {formattedPhone && (
            <a
              href={`tel:${dialNumber}`}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 whitespace-nowrap transition-colors"
            >
              <Phone className="h-4 w-4" />
              {formattedPhone}
            </a>
          )}
        </div>
      </div>
    </header>
  );
}
